import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Box,
	Divider,
	Grid2,
	Skeleton,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Group, Panel, Separator } from "react-resizable-panels";

import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { useDirtyValues } from "@/shared/hooks/useDirtyValues";
import { CLIP_INITIAL_VALUES, Clip } from "@/shared/models/Clip";
import {
	ALL_PRODUCT_AREAS,
	GALV_AREAS,
	PRODUCT_INITIAL_VALUES,
	Product,
	ProductAncestorRef,
	ProductArea,
	ProductChildRef,
	VALID_PARENT_AREAS,
	PRODUCT_AREA_ASSET_ID,
	PRODUCT_AREA_LABEL,
	canHaveParent,
	getProductArea,
	isValidParentArea,
} from "@/shared/models/Product";
import {
	ensurePerforationGroups,
	getProductDetail,
	getProducts,
	setProductParent,
} from "@/shared/repositories/ProductRepository";
import { CatalogSectionSelector } from "@/shared/selectors/CatalogSectionSelector";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { Button, CircularProgress } from "@mui/material";
import { useProductMutations } from "../hooks/useProductMutations";
import { DrawingViewer, DrawingViewerHandle } from "./DrawingViewer";
import { FinishSpecsFields } from "./FinishSpecsFields";
import { PaintSpecsFields } from "./PaintSpecsFields";
import { ProductLineageBar } from "./ProductLineageBar";
import { ProductPickerModal } from "./ProductPickerModal";
import { PunchSpecsFields } from "./PunchSpecsFields";
import { SawSpecsFields } from "./SawSpecsFields";
import { WaxSpecsFields } from "./WaxSpecsFields";
import { WeldSpecsFields } from "./WeldSpecsFields";

type Props = {
	productId: number | null;
	initialAreaId?: number;
	onClose: (shouldRefresh?: boolean) => void;
	onNavigateProduct?: (productId: number) => void;
};

/** Walk up from `parentId` via ParentProductId to build the root-first ancestor
 * chain expected by the productDetail payload (Depth=1 at the direct parent). */
const buildAncestorsForParent = (
	parentId: number,
	allProducts: Product[] | undefined,
): ProductAncestorRef[] => {
	if (!allProducts) return [];
	const byId = new Map<number, Product>();
	for (const p of allProducts) {
		if (p.ProductId != null) byId.set(p.ProductId, p);
	}
	const chain: Product[] = [];
	const visited = new Set<number>();
	let current = byId.get(parentId);
	while (
		current &&
		current.ProductId != null &&
		!visited.has(current.ProductId)
	) {
		visited.add(current.ProductId);
		chain.push(current);
		if (current.ParentProductId == null) break;
		current = byId.get(current.ParentProductId);
	}
	return chain
		.slice()
		.reverse()
		.map((p, i) => ({
			Depth: chain.length - i,
			ProductId: p.ProductId!,
			PartNumber: p.PartNumber || "",
			ExtendedPartNumber: p.ExtendedPartNumber,
			AreaAssetId: p.AreaAssetId,
		}));
};

/** BFS down from `rootId` using ParentProductId from the catalog list. */
const collectDescendants = (
	rootId: number | null,
	allProducts: Product[] | undefined,
): Set<number> => {
	const out = new Set<number>();
	if (rootId == null || !allProducts) return out;
	const childrenByParent = new Map<number, number[]>();
	for (const p of allProducts) {
		if (p.ParentProductId != null && p.ProductId != null) {
			const arr = childrenByParent.get(p.ParentProductId) ?? [];
			arr.push(p.ProductId);
			childrenByParent.set(p.ParentProductId, arr);
		}
	}
	const queue: number[] = [rootId];
	while (queue.length > 0) {
		const id = queue.shift()!;
		for (const cid of childrenByParent.get(id) ?? []) {
			if (!out.has(cid)) {
				out.add(cid);
				queue.push(cid);
			}
		}
	}
	return out;
};

export const EditSpecsModal = ({
	productId,
	initialAreaId,
	onClose,
	onNavigateProduct,
}: Props) => {
	const queryClient = useQueryClient();

	const [selectedClip, setSelectedClip] = useState<Clip | null>(null);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [pickerMode, setPickerMode] = useState<"parent" | "child" | null>(null);
	const closeAfterSaveRef = useRef(false);
	const lineageDirtyRef = useRef(false);
	const firstLoadRef = useRef(true);
	const viewerRef = useRef<DrawingViewerHandle>(null);
	// Mirrors the viewer's own dirty state (zoom/pan vs saved view).
	const [viewDirty, setViewDirty] = useState(false);

	const { data: product, isLoading } = useQuery<Product, Error>({
		queryKey: ["productDetail", productId, initialAreaId],
		queryFn: () =>
			getProductDetail(productId!, initialAreaId ?? PRODUCT_AREA_ASSET_ID.weld),
		enabled: productId !== null,
		onError: (e: Error) => setError(e.message),
	});

	const area: ProductArea = product ? getProductArea(product) : null;
	const areaAssetId = area
		? PRODUCT_AREA_ASSET_ID[area]
		: PRODUCT_AREA_ASSET_ID.weld;
	const isGalv = area != null && GALV_AREAS.has(area);
	const hasDrawingPanel = area != null && area !== "saw" && !isGalv;

	const { data: allProducts } = useQuery<Product[], Error>({
		queryKey: ["products"],
		queryFn: () => getProducts(),
		enabled: pickerMode !== null,
		onError: (e: Error) => setError(e.message),
	});

	const descendants = useMemo(
		() => collectDescendants(productId, allProducts),
		[productId, allProducts],
	);

	const ancestorIds = useMemo(
		() => new Set((product?.Ancestors ?? []).map((a) => a.ProductId)),
		[product],
	);

	const parentPickerDisabledAreas = useMemo(() => {
		if (!area) return undefined;
		const validSet = new Set(VALID_PARENT_AREAS[area]);
		const disabled: Partial<Record<Exclude<ProductArea, null>, string>> = {};
		for (const a of ALL_PRODUCT_AREAS) {
			if (!validSet.has(a)) {
				disabled[a] =
					`${PRODUCT_AREA_LABEL[a]} products cannot be parent of a ${PRODUCT_AREA_LABEL[area]} product`;
			}
		}
		return Object.keys(disabled).length > 0 ? disabled : undefined;
	}, [area]);

	const childPickerDisabledAreas = useMemo(() => {
		if (!area) return undefined;
		const disabled: Partial<Record<Exclude<ProductArea, null>, string>> = {};
		for (const a of ALL_PRODUCT_AREAS) {
			if (!isValidParentArea(a, area)) {
				disabled[a] =
					`${PRODUCT_AREA_LABEL[a]} products cannot be child of a ${PRODUCT_AREA_LABEL[area]} product`;
			}
		}
		return Object.keys(disabled).length > 0 ? disabled : undefined;
	}, [area]);

	// Exclude self + descendants, then enforce process flow area compatibility.
	const validParentOptions = useMemo(() => {
		if (!allProducts || !area) return [] as Product[];
		const validParentAreas = new Set(VALID_PARENT_AREAS[area]);
		return allProducts.filter((p) => {
			if (p.ProductId == null || p.ProductId === productId) return false;
			if (descendants.has(p.ProductId)) return false;
			const parentArea = getProductArea(p);
			if (parentArea == null || !validParentAreas.has(parentArea)) return false;
			return true;
		});
	}, [allProducts, area, productId, descendants]);

	// Exclude self, ancestors, already-linked children, and incompatible areas.
	const validChildOptions = useMemo(() => {
		if (!allProducts || !area) return [] as Product[];
		return allProducts.filter((p) => {
			if (p.ProductId == null || p.ProductId === productId) return false;
			if (ancestorIds.has(p.ProductId)) return false;
			if (product?.Children?.some((c) => c.ProductId === p.ProductId))
				return false;
			const childArea = getProductArea(p);
			if (!isValidParentArea(childArea, area)) return false;
			return true;
		});
	}, [allProducts, area, productId, ancestorIds, product?.Children]);

	const detailQueryKey = useMemo(
		() => ["productDetail", productId, initialAreaId] as const,
		[productId, initialAreaId],
	);

	const patchDetailCache = useCallback(
		(updater: (p: Product) => Product) => {
			queryClient.setQueryData<Product>(
				detailQueryKey as unknown as readonly unknown[],
				(old) => (old ? updater(old) : old),
			);
			lineageDirtyRef.current = true;
		},
		[queryClient, detailQueryKey],
	);

	const patchProductsCache = useCallback(
		(updater: (list: Product[]) => Product[]) => {
			queryClient.setQueryData<Product[]>(["products"], (old) =>
				old ? updater(old) : old,
			);
		},
		[queryClient],
	);

	const parentMutation = useMutation({
		mutationFn: (newParentId: number | null) =>
			setProductParent(productId!, newParentId),
		onSuccess: (resp, newParentId) => {
			if (!resp.ok) {
				setError(resp.message || "Error setting parent product");
				return;
			}
			setNotificationMessage("Parent updated");
			const newParent =
				newParentId != null
					? allProducts?.find((p) => p.ProductId === newParentId)
					: null;
			const newAncestors =
				newParentId != null
					? buildAncestorsForParent(newParentId, allProducts)
					: [];
			patchDetailCache((p) => ({
				...p,
				ParentProductId: newParentId,
				ParentPartNumber: newParent?.PartNumber ?? null,
				Ancestors: newAncestors,
			}));
			if (productId != null) {
				patchProductsCache((list) =>
					list.map((p) =>
						p.ProductId === productId
							? { ...p, ParentProductId: newParentId }
							: p,
					),
				);
			}
		},
		onError: (e: Error) => setError(e.message),
	});

	const removeChildMutation = useMutation({
		mutationFn: (childId: number) => setProductParent(childId, null),
		onSuccess: (resp, childId) => {
			if (!resp.ok) {
				setError(resp.message || "Error removing child");
				return;
			}
			setNotificationMessage("Child removed");
			patchDetailCache((p) => ({
				...p,
				Children: p.Children.filter((c) => c.ProductId !== childId),
			}));
			patchProductsCache((list) =>
				list.map((p) =>
					p.ProductId === childId ? { ...p, ParentProductId: null } : p,
				),
			);
		},
		onError: (e: Error) => setError(e.message),
	});

	const addChildMutation = useMutation({
		mutationFn: (childId: number) => setProductParent(childId, productId!),
		onSuccess: (resp, childId) => {
			if (!resp.ok) {
				setError(resp.message || "Error adding child");
				return;
			}
			setNotificationMessage("Child added");
			const child = allProducts?.find((p) => p.ProductId === childId);
			if (child) {
				const newChild: ProductChildRef = {
					ProductId: childId,
					PartNumber: child.PartNumber || "",
					ExtendedPartNumber: child.ExtendedPartNumber,
					AreaAssetId: child.AreaAssetId,
				};
				patchDetailCache((p) => ({
					...p,
					Children: [...p.Children, newChild],
				}));
			}
			if (productId != null) {
				patchProductsCache((list) =>
					list.map((p) =>
						p.ProductId === childId ? { ...p, ParentProductId: productId } : p,
					),
				);
			}
		},
		onError: (e: Error) => setError(e.message),
	});

	const form = useForm<Product>({ defaultValues: PRODUCT_INITIAL_VALUES });
	const { control, reset, handleSubmit, setValue, watch, formState } = form;
	const getDirtyValues = useDirtyValues(form);

	const currentDrawingPath = watch("DrawingPath");

	useEffect(() => {
		firstLoadRef.current = true;
		lineageDirtyRef.current = false;
	}, [productId]);

	const closeWithRefresh = useCallback(
		(saved: boolean) => {
			const shouldRefresh = saved || lineageDirtyRef.current;
			lineageDirtyRef.current = false;
			onClose(shouldRefresh);
		},
		[onClose],
	);

	useEffect(() => {
		if (product) {
			const isPunch = product.AreaAssetId === PRODUCT_AREA_ASSET_ID.punch;
			const hydratedProduct =
				isPunch && firstLoadRef.current
					? {
							...product,
							Perforations: ensurePerforationGroups(
								product.Perforations,
								product.ProductId ?? 0,
							),
						}
					: product;
			firstLoadRef.current = false;
			reset(hydratedProduct);
			if (product.ClipId) {
				setSelectedClip({
					...CLIP_INITIAL_VALUES,
					ClipId: product.ClipId,
					ClipPartNumber: product.ClipPartNumber,
					Width: product.ClipWidth || 0,
					Height: product.ClipHeight || 0,
					HolePattern: product.HolePattern || "",
				});
			} else {
				setSelectedClip(null);
			}
		}
	}, [product, reset]);

	const { upsertMutation, isPending } = useProductMutations({
		getDirtyValues,
		activeAreaId: areaAssetId,
		weldsAreaId: PRODUCT_AREA_ASSET_ID.weld,
		sawsAreaId: PRODUCT_AREA_ASSET_ID.saw,
		paintAreaId: PRODUCT_AREA_ASSET_ID.paint,
		punchAreaId: PRODUCT_AREA_ASSET_ID.punch,
		waxAreaId: PRODUCT_AREA_ASSET_ID.wax,
		onSuccess: (message, _productId, droppedRows) => {
			const finalMessage =
				droppedRows && droppedRows > 0
					? `${message} (${droppedRows} empty row${droppedRows === 1 ? "" : "s"} discarded)`
					: message;
			setNotificationMessage(finalMessage);
			reset(form.getValues());
			// Mark the session dirty so any later close path (Cancel / X) refetches.
			lineageDirtyRef.current = true;
			if (closeAfterSaveRef.current) {
				closeAfterSaveRef.current = false;
				closeWithRefresh(true);
			}
		},
		onError: setError,
	});

	const onSubmit = async (data: Product) => {
		if (viewDirty && (await viewerRef.current?.saveView()) === false) return;
		// A view-only change has nothing to upsert; don't touch the product row.
		if (!formState.isDirty) {
			setNotificationMessage("The drawing view was saved");
			if (closeAfterSaveRef.current) {
				closeAfterSaveRef.current = false;
				closeWithRefresh(false);
			}
			return;
		}
		upsertMutation.mutate(data);
	};

	const handleDrawingUpload = useCallback(
		(url: string) => {
			setValue("DrawingPath", url, { shouldDirty: true });
		},
		[setValue],
	);

	const handleDrawingClear = useCallback(() => {
		setValue("DrawingPath", null, { shouldDirty: true });
	}, [setValue]);

	const handleClipChange = (_clipId: number | null, clip: Clip | null) => {
		setSelectedClip(clip);
	};

	const isOpen = productId !== null;

	return (
		<>
			<MesfModal
				id="modal-edit-specs"
				title="EDIT PRODUCT"
				open={isOpen}
				handleClose={() => closeWithRefresh(false)}
				maxWidth={false}
			>
				<MesfModal.Content style={{ padding: "15px 24px", width: 1500 }}>
					{product && (
						<Box sx={{ mb: 2 }}>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{
									fontWeight: 700,
									letterSpacing: "0.08em",
									textTransform: "uppercase",
									display: "block",
									mb: 1,
									fontSize: "0.62rem",
								}}
							>
								Lineage
							</Typography>
							<ProductLineageBar
								product={product}
								onNavigate={() => {}}
								disableNavigate
								edit={{
									canAddParent: canHaveParent(area),
									onAddParentClick: () => setPickerMode("parent"),
									onRemoveParent: () => parentMutation.mutate(null),
									onAddChildClick: () => setPickerMode("child"),
									onRemoveChild: (id) => removeChildMutation.mutate(id),
									isDisabled:
										parentMutation.isPending ||
										addChildMutation.isPending ||
										removeChildMutation.isPending,
								}}
							/>
						</Box>
					)}

					<Divider sx={{ mb: 1.5 }} />

					<Grid2 container spacing={1.5} sx={{ mb: 1.5 }}>
						<Grid2 size={{ xs: 4 }}>
							<Controller
								name="PartNumber"
								control={control}
								rules={{ required: "Required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										label={
											area === "saw" || isGalv ? "Part Number" : "Beam Part #"
										}
										value={field.value || ""}
										onChange={field.onChange}
										error={!!error}
										fullWidth
										size="small"
										disabled={isPending}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 4 }}>
							<Controller
								name="ExtendedPartNumber"
								control={control}
								rules={{ required: "Required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										label={
											area === "saw" || isGalv
												? "Extended Part Number"
												: "Beam Extended Part #"
										}
										value={field.value || ""}
										onChange={(e) => field.onChange(e.target.value || null)}
										error={!!error}
										helperText={error?.message}
										fullWidth
										size="small"
										disabled={isPending}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 4 }}>
							<Controller
								name="GenericName"
								control={control}
								render={({ field }) => (
									<TextField
										label="Name"
										value={field.value || ""}
										onChange={(e) => field.onChange(e.target.value || null)}
										fullWidth
										size="small"
										disabled={isPending}
									/>
								)}
							/>
						</Grid2>
						{area === "saw" && (
							<>
								<Grid2 size={{ xs: 4 }}>
									<Controller
										name="SectionId"
										control={control}
										render={({ field, fieldState: { error } }) => (
											<CatalogSectionSelector
												label="Section"
												value={field.value}
												onChange={field.onChange}
												fieldError={error}
												disabled={isPending}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={{ xs: 4 }}>
									<Controller
										name="GradeId"
										control={control}
										render={({ field, fieldState: { error } }) => (
											<GradeSelector
												label="Grade"
												value={field.value}
												onChange={field.onChange}
												fieldError={error}
												disabled={isPending}
											/>
										)}
									/>
								</Grid2>
							</>
						)}
					</Grid2>

					<Divider sx={{ mb: 1.5 }} />

					{isLoading ? (
						<Box sx={{ height: "60vh", display: "flex", gap: 2 }}>
							<Box
								sx={{
									width: "60%",
									display: "flex",
									flexDirection: "column",
									gap: 2,
									pt: 1,
								}}
							>
								<Skeleton variant="text" width="40%" height={28} />
								<Skeleton variant="rectangular" height={40} />
								<Skeleton variant="rectangular" height={40} />
								<Skeleton variant="rectangular" height={40} />
								<Skeleton
									variant="text"
									width="50%"
									height={28}
									sx={{ mt: 1 }}
								/>
								<Skeleton variant="rectangular" height={40} />
								<Skeleton variant="rectangular" height={40} />
							</Box>
							{hasDrawingPanel && (
								<Skeleton
									variant="rectangular"
									sx={{ flex: 1, borderRadius: 1 }}
									height="100%"
								/>
							)}
						</Box>
					) : (
						(() => {
							const formContent = (
								<Box
									sx={{
										overflow: "auto",
										height: "100%",
										pr: hasDrawingPanel ? 1 : 0,
									}}
								>
									<Grid2 container spacing={1.5} columnSpacing={1.5}>
										{area === "weld" && (
											<WeldSpecsFields
												control={control}
												setValue={setValue}
												disabled={isPending}
												selectedClip={selectedClip}
												onClipChange={handleClipChange}
												parentSawCutLength={product?.ParentSawCutLength ?? null}
											/>
										)}
										{area === "wax" && (
											<WaxSpecsFields
												control={control}
												setValue={setValue}
												disabled={isPending}
											/>
										)}
										{area === "paint" && (
											<PaintSpecsFields
												control={control}
												setValue={setValue}
												disabled={isPending}
											/>
										)}
										{area === "punch" && (
											<PunchSpecsFields
												control={control}
												setValue={setValue}
												disabled={isPending}
											/>
										)}
										{area === "saw" && (
											<SawSpecsFields control={control} disabled={isPending} />
										)}
										{(area === "galv-welded" || area === "galv-punched") && (
											<WaxSpecsFields
												control={control}
												setValue={setValue}
												disabled={isPending}
											/>
										)}
										{area != null && (
											<FinishSpecsFields
												control={control}
												disabled={isPending}
												areaAssetId={areaAssetId}
											/>
										)}
										{area === "weld" && (
											<>
												<Grid2 size={{ xs: 12 }}>
													<Typography
														variant="subtitle2"
														fontWeight={600}
														color="primary"
														sx={{ mb: 0.5, mt: 1 }}
													>
														Comments
													</Typography>
													<Divider />
												</Grid2>
												<Grid2 size={{ xs: 12 }}>
													<Controller
														name="WeldComments"
														control={control}
														render={({ field }) => (
															<TextField
																label="Weld Comments"
																value={field.value || ""}
																onChange={field.onChange}
																fullWidth
																size="small"
																disabled={isPending}
																multiline
																rows={2}
															/>
														)}
													/>
												</Grid2>
											</>
										)}
										{area == null && (
											<Grid2 size={{ xs: 12 }}>
												<Typography color="text.secondary">
													This product has no area assigned yet. Specs cannot be
													edited until an area exists.
												</Typography>
											</Grid2>
										)}
									</Grid2>
								</Box>
							);

							return (
								<Box sx={{ height: "60vh" }}>
									{hasDrawingPanel ? (
										<Group orientation="horizontal" style={{ height: "100%" }}>
											<Panel id="edit-specs-form" defaultSize={60} minSize={30}>
												{formContent}
											</Panel>
											<Separator
												style={{
													width: 8,
													backgroundColor: "#e0e0e0",
													cursor: "col-resize",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
												}}
											>
												<Box
													sx={{
														width: 4,
														height: 40,
														backgroundColor: "#bdbdbd",
														borderRadius: 2,
													}}
												/>
											</Separator>
											<Panel
												id="edit-specs-drawing"
												defaultSize={40}
												minSize={20}
											>
												<DrawingViewer
													ref={viewerRef}
													productId={productId}
													drawingPath={currentDrawingPath || null}
													onDrawingUpload={handleDrawingUpload}
													onDrawingClear={handleDrawingClear}
													onDirtyChange={setViewDirty}
													onError={setError}
													toolbarLeft={
														<Controller
															name="DrawingNumber"
															control={control}
															render={({ field }) => (
																<TextField
																	{...field}
																	value={field.value ?? ""}
																	label="Drawing Number"
																	size="small"
																	disabled={isPending}
																	sx={{ minWidth: 180 }}
																/>
															)}
														/>
													}
												/>
											</Panel>
										</Group>
									) : (
										<Box sx={{ width: "100%", height: "100%" }}>
											{formContent}
										</Box>
									)}
								</Box>
							);
						})()
					)}
				</MesfModal.Content>

				<MesfModal.Actions
					style={{
						padding: "16px 24px 24px",
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
					}}
				>
					<Button
						variant="contained"
						color="inherit"
						onClick={() => closeWithRefresh(false)}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						disabled={isPending || (!formState.isDirty && !viewDirty)}
						startIcon={
							isPending && !closeAfterSaveRef.current ? (
								<CircularProgress size="1rem" />
							) : undefined
						}
						onClick={() => {
							closeAfterSaveRef.current = false;
							handleSubmit(onSubmit)();
						}}
					>
						Save
					</Button>
					<Button
						variant="contained"
						color="primary"
						disabled={isPending || (!formState.isDirty && !viewDirty)}
						startIcon={
							isPending && closeAfterSaveRef.current ? (
								<CircularProgress size="1rem" />
							) : undefined
						}
						onClick={() => {
							closeAfterSaveRef.current = true;
							handleSubmit(onSubmit)();
						}}
					>
						Save & Close
					</Button>
				</MesfModal.Actions>
			</MesfModal>

			<ProductPickerModal
				open={pickerMode !== null}
				title={pickerMode === "parent" ? "Select Parent" : "Select Child"}
				products={
					pickerMode === "parent" ? validParentOptions : validChildOptions
				}
				disabledAreas={
					pickerMode === "parent"
						? parentPickerDisabledAreas
						: childPickerDisabledAreas
				}
				// Child-picker: replacing existing parent needs confirmation
				checkParentConflict={pickerMode === "child"}
				onSelect={(id) => {
					if (pickerMode === "parent") {
						parentMutation.mutate(id);
					} else if (pickerMode === "child") {
						addChildMutation.mutate(id);
					}
					setPickerMode(null);
				}}
				onClose={() => setPickerMode(null)}
			/>

			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
